// Shingan and his decoys drawn from the Art Studio sheets (client/art/shingan, `npm run art:shingan`), on by default since
// 2026-10-18 (Cr3L; `?shinganart=0` shows the painted abbot). Presentation only: every state is read from what the snapshot already says (position,
// facing, hp, `sg.wu`, `sg.bl`, the shockwave event); the server decides everything. Without the sheets the caller keeps the
// painted abbot.
import { Sprite, Container, Graphics, Matrix, AlphaFilter } from 'pixi.js';
import { loadSpriteSet, facingSlot, frameAt, type SpriteSet } from './sprite-sheet';
import type { NpcSnap, Snapshot } from '../../common/src/protocol';

export const SHINGAN_ART = typeof location !== 'undefined' && new URLSearchParams(location.search).get('shinganart') !== '0';

interface St { anim: string; t0: number; face: number; x: number; y: number; hp: number; wu: number; bl: string; lastMove: number }

export class ShinganArt {
  set?: SpriteSet; private st = new Map<number, St>(); private shock = -9; private provoked = false; private ghosts = new Map<string, Sprite>();
  private glows = new Map<number, Sprite>(); private touched = new Set<number>();
  /** dev/screenshot hook: hold every body on one animation frame (scripts/shingan-shots.mjs); never set by the game */
  freeze?: { anim: string; time: number; face?: number };
  constructor(base: string, v: string, private glowLayer?: Container) { loadSpriteSet(base, v).then(s => { this.set = s; }).catch(e => console.warn('shinganart: sheets not loaded', e)); }
  get ready() { return !!this.set; }
  /** the shockwave event (boom elec) at his feet */
  boom(t: number) { this.shock = t; }

  /** Choose the animation for one body this frame. `isBoss` is false for decoys, which only stand, walk and meditate like him. */
  pick(n: NpcSnap, sg: NonNullable<Snapshot['sg']>, isBoss: boolean, t: number, dt: number): { anim: string; time: number; face: number } {
    let s = this.st.get(n.id);
    if (!s) { s = { anim: 'idle', t0: t, face: facingSlot(n.a), x: n.x, y: n.y, hp: n.hp, wu: sg.wu, bl: '', lastMove: -9 }; this.st.set(n.id, s); }
    if (isBoss && (n.st === 2 || sg.wu || n.hp < n.mhp)) this.provoked = true; // once roused he never goes back to his seat
    const speed = Math.hypot(n.x - s.x, n.y - s.y) / Math.max(dt, 1e-3), blink = isBoss && sg.bl ? sg.bl.join(',') : '';
    const set = (a: string) => { if (s!.anim !== a) { s!.anim = a; s!.t0 = t; } };
    const el = t - s.t0, busy = (a: string) => s!.anim === a && el < this.dur(a);
    if (n.hp <= 0) set('defeat');
    else {
      if (isBoss) {
        if (sg.wu && !s.wu) { set('windup'); }
        else if (!sg.wu && s.wu) set('sweep');
        else if (t - this.shock < 0.5 && s.anim !== 'shockwave') set('shockwave');
        else if (blink && blink !== s.bl) { set('blinkIn'); this.ghost(sg.bl!, t); }
      }
      if (n.hp < s.hp - 0.5 && !busy('windup') && !busy('shockwave')) set('hit');
      const hold = sg.wu && isBoss;
      if (!hold && !busy('sweep') && !busy('shockwave') && !busy('blinkIn') && !busy('hit') && s.anim !== 'defeat') {
        if (speed > 8) { s.lastMove = t; set('walk'); } else set(this.provoked ? 'idle' : 'meditate');
      }
    }
    if (s.anim === 'walk' && speed <= 8 && t - s.lastMove > 0.12) set('idle');
    s.face = facingSlot(n.a, 16, s.face); s.x = n.x; s.y = n.y; s.hp = n.hp; s.wu = sg.wu; s.bl = blink;
    if (this.freeze) return { anim: this.freeze.anim, time: this.freeze.time, face: this.freeze.face ?? s.face };
    return { anim: s.anim, time: t - s.t0, face: s.face };
  }
  private dur(a: string) { const an = this.set?.anims.get(a); return an ? an.ms.reduce((x, m) => x + m, 0) / 1000 : 0; }
  private ghost(from: [number, number], t: number) { this.ghosts.set(from.join(','), Object.assign(new Sprite(), { t0: t, fx: from[0], fy: from[1] })); }

  /** Put the right frame on `sp` (a Sprite anchored by this call); returns false if the sheets are not ready. */
  apply(sp: Sprite, x: number, y: number, c: { anim: string; time: number; face: number }): boolean {
    const set = this.set, an = set?.anims.get(c.anim);
    if (!set || !an) return false;
    const f = frameAt(an, c.time);
    sp.texture = an.frames[f][c.face] ?? an.frames[f][0];
    sp.anchor.set(set.anchor[0] / set.size[0], set.anchor[1] / set.size[1]); sp.scale.set(0.5);
    sp.x = x; sp.y = y + 3; sp.zIndex = y + 6; sp.visible = true;
    return true;
  }
  /** The unlit overlay of one body (mask, slit, seam and blade edge at full brightness above the lighting). Call after pply. */
  glow(id: number, sp: Sprite, c: { anim: string; time: number; face: number }) {
    const an = this.set?.anims.get(c.anim);
    if (!an?.glow || !this.glowLayer) return;
    const f = frameAt(an, c.time);
    let g = this.glows.get(id);
    if (!g) { g = new Sprite(); this.glows.set(id, g); }
    g.texture = an.glow[f][c.face] ?? an.glow[f][0]; g.anchor.copyFrom(sp.anchor);
    this.bodies.push({ g, body: sp, z: sp.zIndex, flash: sp.tint !== 0xffffff });
    this.touched.add(id);
  }
  private bodies: { g: Sprite; body: Sprite; z: number; flash: boolean }[] = [];
  private cuts: Sprite[] = []; private cutG = new Map<unknown, Graphics[]>(); private mtx = new Matrix();
  /**
   * Draw the recorded overlays, each with what stands in front of it cut away. The overlay must sit above the lighting filter
   * (in `glowLayer`, a stage-level container isolated by its own alpha filter), so it cannot live in the sorted actor layer.
   * Instead, after each body's overlay (back to front), every sprite that sorts in front of that body (a higher zIndex in the
   * actor layer, or anything in a layer drawn above it) is drawn again with the 'erase' blend at its exact screen transform:
   * a runner, lamp or tower top in front hides the highlights exactly where it hides the body. `strength` is the
   * comfort-graded alpha, `tint` the local night colour (a hit flash keeps the body's tint).
   */
  endFrame(occluders: { layer: Container; above: boolean }[], strength: number, tint: number) {
    for (const [id, g] of this.glows) if (!this.touched.has(id)) { g.destroy(); this.glows.delete(id); }
    this.touched.clear();
    const root = this.glowLayer; if (!root) { this.bodies.length = 0; return; }
    root.removeChildren();
    let nc = 0; const ng = new Map<unknown, number>();
    const cut = (s: Sprite | Graphics, alpha: number) => {
      let c: Sprite | Graphics;
      if (s instanceof Sprite) { const sp = this.cuts[nc] ??= Object.assign(new Sprite(), { blendMode: 'erase' as const }); nc++; sp.texture = s.texture; sp.anchor.copyFrom(s.anchor); c = sp; }
      else { const k = s.context, i = ng.get(k) ?? 0, list = this.cutG.get(k) ?? []; this.cutG.set(k, list); c = list[i] ??= Object.assign(new Graphics(k), { blendMode: 'erase' as const }); ng.set(k, i + 1); }
      c.setFromMatrix(this.globalOf(s)); c.alpha = alpha; root.addChild(c);
    };
    const walk = (n: Container, alpha: number, bx: number, by: number, skip: Sprite) => {
      if (!n.visible || n === skip || alpha * n.alpha < 0.05) return;
      const a = alpha * n.alpha;
      if (n instanceof Sprite || n instanceof Graphics) {
        const b = n.getBounds(); if (b.maxX < bx - 90 || b.minX > bx + 90 || b.maxY < by - 130 || b.minY > by + 50) return; // only near the body (screen px)
        cut(n, a); return;
      }
      for (const ch of n.children) walk(ch, a, bx, by, skip);
    };
    this.bodies.sort((a, b) => a.z - b.z);
    for (const { g, body, z, flash } of this.bodies) {
      const m = this.globalOf(body), bx = m.tx, by = m.ty;
      g.setFromMatrix(m); g.tint = flash ? body.tint : tint; root.addChild(g);
      for (const o of occluders) for (const ch of o.layer.children) {
        if (ch === body) continue;
        // in the sorted actor layer: only what stands in front (higher zIndex) and near his feet (cheap world-space test first)
        if (!o.above && (ch.zIndex <= z || Math.abs(ch.x - body.x) > 70 || ch.y < body.y - 30 || ch.y > body.y + 160)) continue;
        walk(ch, 1, bx, by, body);
      }
    }
    this.bodies.length = 0;
    const f = root.filters?.[0]; if (f instanceof AlphaFilter) f.alpha = strength;
  }
  /** the screen (stage) transform of a node, from this frame's local transforms (not last render's world transforms) */
  private globalOf(n: Container): Matrix {
    const m = this.mtx.identity();
    for (let c: Container | null = n; c; c = c.parent) { c.updateLocalTransform(); m.prepend(c.localTransform); }
    return m;
  }
  /** Fading afterimage where he blinked from: the blinkOut frames at that point (pool-free; at most a few per blink). */
  drawGhosts(t: number, add: (s: Sprite) => void, remove: (s: Sprite) => void, face: number) {
    const an = this.set?.anims.get('blinkOut');
    for (const [k, s] of this.ghosts) {
      const g = s as Sprite & { t0: number; fx: number; fy: number };
      const el = t - g.t0;
      if (!an || el > 0.3) { remove(s); s.destroy(); this.ghosts.delete(k); continue; }
      if (!s.parent) add(s);
      this.apply(s, g.fx, g.fy, { anim: 'blinkOut', time: el, face });
    }
  }
  prune(live: Set<number>) { for (const id of this.st.keys()) if (!live.has(id)) this.st.delete(id); }
}
